/**
 * 報酬台帳画面 (v3.2.8 / T6)。
 *
 * URL: /sales/commission-ledger
 *
 * 確定済みの報酬レコードを一覧表示 (read-only Phase 1)。
 * 後続 Phase: 編集モーダル / 支払い申請メール / 請求書アップロード。
 *
 * 2026-10-30: 種類・状態の絞り込みを URL (?ledgerType= / ?ledgerStatus=) に持たせ、商談名のリンクは戻り先 (報酬台帳) を渡す。
 *   商談詳細の「前の画面へ」で、同じ絞り込みのままここへ戻れる (チェックの選択は戻さない)。
 */
import type { CommissionListType, CommissionRecordStatus, DealStage } from '@goenhub/shared';
import {
  COMMISSION_LIST_LABELS,
  COMMISSION_RECORD_STATUS_LABELS,
  resolveAccountingNotifyChannels,
} from '@goenhub/shared';

const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  document_requested: '資料請求',
  briefing: '個別説明',
  quote: '見積',
  won: '成約',
  agreed: '同意',
  paid: '入金',
  ordered: '発注',
  delivered: '発送・納品',
  lost: '失注',
};
import { Bell, CheckCircle, Coins, Filter, Mail, Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { EditRecordDialog } from './EditRecordDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useSendCommissionInvoiceReminder } from '@/hooks/useCommissionInvoices';
import {
  useCommissionRecords,
  useDealStagesByIds,
  useMarkCommissionRecordPaid,
  useRequestCommissionPayment,
  type CommissionRecordWithRef,
} from '@/hooks/useCommissionRecords';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useSalesSettings } from '@/hooks/useSettings';
import { useUrlEnumParam } from '@/hooks/useUrlParam';
import { paths } from '@/routes/paths';

const TYPE_FILTER_OPTIONS: Array<{
  value: CommissionListType | 'all';
  label: string;
}> = [
  { value: 'all', label: 'すべて' },
  { value: 'referrer', label: '紹介者' },
  { value: 'sa', label: 'SA' },
  { value: 'employee', label: '社員' },
];

const STATUS_FILTER_OPTIONS: Array<{
  value: CommissionRecordStatus | 'all';
  label: string;
}> = [
  { value: 'all', label: 'すべて' },
  { value: 'pending', label: '未申請' },
  { value: 'requested', label: '申請中' },
  { value: 'paid', label: '支払済' },
];

const TYPE_FILTER_VALUES = TYPE_FILTER_OPTIONS.map((o) => o.value);
const STATUS_FILTER_VALUES = STATUS_FILTER_OPTIONS.map((o) => o.value);

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function statusBadgeVariant(s: CommissionRecordStatus) {
  if (s === 'paid') return 'approved' as const;
  if (s === 'requested') return 'review' as const;
  return 'default' as const;
}

export function CommissionLedgerPage() {
  const records = useCommissionRecords();
  const requestPayment = useRequestCommissionPayment();
  const markPaid = useMarkCommissionRecordPaid();
  const sendReminder = useSendCommissionInvoiceReminder();
  const salesSettings = useSalesSettings();
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useUrlEnumParam('ledgerType', TYPE_FILTER_VALUES, 'all');
  const [statusFilter, setStatusFilter] = useUrlEnumParam(
    'ledgerStatus',
    STATUS_FILTER_VALUES,
    'all',
  );
  const returnToState = useReturnToState('報酬台帳');
  const [editing, setEditing] = useState<CommissionRecordWithRef | null>(null);

  const handleRequest = async (r: CommissionRecordWithRef) => {
    if (!r.bankAccountId && r.recipientType !== 'referrer') {
      toast.error('支払先口座を先に選択してください (編集ボタンから)');
      return;
    }
    // 2026-10-26: 送り方は 営業・通知設定 > 自動通知「報酬の支払い申請」(メール / GC / CW) に従う
    const channels = resolveAccountingNotifyChannels(
      salesSettings.data?.accountingNotify,
      'commission_payment_request',
    );
    const accountingEmail = salesSettings.data?.accountingEmail;
    if (!channels.mail && !channels.googleChat && !channels.chatwork) {
      toast.error('「報酬の支払い申請」の通知がすべてオフです（営業・通知設定 > 自動通知）');
      return;
    }
    if (channels.mail && !accountingEmail) {
      toast.error('経理担当メールアドレスが営業設定で未設定です');
      return;
    }
    const via = [
      channels.mail ? `メール (${accountingEmail})` : null,
      channels.googleChat ? 'Google Chat' : null,
      channels.chatwork ? 'Chatwork' : null,
    ]
      .filter(Boolean)
      .join(' / ');
    const msg = `経理担当へ「${r.recipientName} さんへの支払い申請」を送信しますか？\n送り方: ${via}`;
    if (!confirm(msg)) return;
    try {
      const res = await requestPayment.mutateAsync({ recordPath: r.path });
      const n = res.notify;
      const label = (o: string) =>
        o === 'sent'
          ? '送信済み'
          : o === 'failed'
            ? '送信失敗'
            : o === 'no_target'
              ? '送信先が未設定'
              : 'オフ';
      const detail = n
        ? [
            n.mail !== 'off' ? `メール ${label(n.mail)}` : null,
            n.googleChat !== 'off' ? `GC ${label(n.googleChat)}` : null,
            n.chatwork !== 'off' ? `CW ${label(n.chatwork)}` : null,
          ]
            .filter(Boolean)
            .join(' / ')
        : '';
      const problem = n
        ? [n.mail, n.googleChat, n.chatwork].some((o) => o === 'failed' || o === 'no_target')
        : !res.ok;
      if (res.ok && !problem) {
        toast.success('経理担当へ支払い申請を送信しました', { description: detail });
      } else if (res.ok) {
        toast.warning('支払い申請を送信しました（一部送れていません）', { description: detail });
      } else {
        toast.warning(`送信失敗: ${res.reason ?? '理由不明'}`, { description: detail });
      }
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const handleMarkPaid = async (r: CommissionRecordWithRef) => {
    if (
      !confirm(
        `${r.recipientName} 宛 ¥${r.finalAmount.toLocaleString()} を支払済としてマークしますか？`,
      )
    )
      return;
    try {
      await markPaid.mutateAsync({ recordPath: r.path });
      toast.success('支払済にマークしました');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const filtered = useMemo(() => {
    return (records.data ?? []).filter((r) => {
      if (typeFilter !== 'all' && r.recipientType !== typeFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      return true;
    });
  }, [records.data, typeFilter, statusFilter]);

  // Phase 2: 各レコードの dealId → 現在の Deal.stage を一括取得
  const dealIds = useMemo(() => (records.data ?? []).map((r) => r.dealId), [records.data]);
  const dealStages = useDealStagesByIds(dealIds);

  // Phase 3b: SA リマインダー対象 = 選択中で SA タイプ かつ pending、かつ同じ SA
  const reminderEligible = useMemo(() => {
    const sel = (records.data ?? []).filter(
      (r) => selectedPaths.has(r.path) && r.recipientType === 'sa' && r.status === 'pending',
    );
    if (sel.length === 0) return null;
    const saIds = new Set(sel.map((r) => r.recipientId));
    if (saIds.size !== 1) return null; // 複数 SA は不可
    return {
      saName: sel[0].recipientName,
      saId: sel[0].recipientId,
      count: sel.length,
      totalAmount: sel.reduce((s, r) => s + r.finalAmount, 0),
      paths: sel.map((r) => r.path),
    };
  }, [records.data, selectedPaths]);

  const handleSendReminder = async () => {
    if (!reminderEligible) return;
    const msg = `SA「${reminderEligible.saName}」宛に ${reminderEligible.count} 件 / 合計 ¥${reminderEligible.totalAmount.toLocaleString()} の請求書発行リマインダーメールを送信しますか？`;
    if (!confirm(msg)) return;
    try {
      const res = await sendReminder.mutateAsync({ recordPaths: reminderEligible.paths });
      if (res.mailSent) {
        toast.success(`${reminderEligible.saName} さんへリマインダーを送信しました`);
        setSelectedPaths(new Set());
      } else {
        toast.warning(`送信失敗: ${res.mailReason ?? '理由不明'}`);
      }
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const toggleSelect = (path: string) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // サマリ
  const summary = useMemo(() => {
    const totals = { pending: 0, requested: 0, paid: 0 };
    for (const r of filtered) {
      totals[r.status] += r.finalAmount;
    }
    return totals;
  }, [filtered]);

  return (
    <Screen
      title="報酬台帳"
      description="紹介者・SA・社員への報酬計算の確定記録。入金タイミングで自動起票され、支払い申請までを管理します。"
      restoreScroll
    >
      <div className="space-y-4">
        {/* サマリ */}
        <div className="grid grid-cols-3 gap-3">
          <SummaryCard label="未申請" value={summary.pending} accent="amber" />
          <SummaryCard label="申請中" value={summary.requested} accent="blue" />
          <SummaryCard label="支払済" value={summary.paid} accent="green" />
        </div>

        {/* フィルタ + リマインダー */}
        <div className="flex items-center gap-3 flex-wrap rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
          <Filter className="size-3.5 text-[var(--text-muted)]" />
          <FilterGroup
            label="受取人"
            options={TYPE_FILTER_OPTIONS}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as CommissionListType | 'all')}
          />
          <FilterGroup
            label="ステータス"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as CommissionRecordStatus | 'all')}
          />
          {reminderEligible && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-[var(--text-muted)]">
                {reminderEligible.saName} {reminderEligible.count} 件 / ¥
                {reminderEligible.totalAmount.toLocaleString()}
              </span>
              <button
                type="button"
                onClick={handleSendReminder}
                disabled={sendReminder.isPending}
                className="px-2.5 py-1 text-xs rounded border border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                <Bell className="size-3" />
                請求リマインダー
              </button>
            </div>
          )}
          {selectedPaths.size > 0 && !reminderEligible && (
            <span className="ml-auto text-[10px] text-[var(--text-muted)] italic">
              ※ 同一 SA の pending レコードを 1 件以上選ぶとリマインダー送信可能
            </span>
          )}
        </div>

        {/* 一覧 */}
        {records.isLoading ? (
          <Spinner />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Coins}
            title="該当する報酬レコードがありません"
            description="商談が入金 (Invoice.status='paid') されると自動起票されます。報酬セットが商品カテゴリに有効化されている必要があります。"
          />
        ) : (
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <div className="grid grid-cols-[32px_70px_2fr_1.2fr_1fr_90px_120px_100px_140px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              <div></div>
              <div>種別</div>
              <div>商談 / 商品</div>
              <div>顧客 / 受取人</div>
              <div>入金日</div>
              <div className="text-right">報酬額</div>
              <div className="text-center">支払先口座</div>
              <div className="text-center">状態</div>
              <div className="text-center">操作</div>
            </div>
            {filtered.map((r) => (
              <div
                key={r.path}
                className="grid grid-cols-[32px_70px_2fr_1.2fr_1fr_90px_120px_100px_140px] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)] hover:bg-[var(--bg-hover)]"
              >
                <div>
                  <input
                    type="checkbox"
                    checked={selectedPaths.has(r.path)}
                    onChange={() => toggleSelect(r.path)}
                    disabled={r.recipientType !== 'sa' || r.status !== 'pending'}
                    title={
                      r.recipientType !== 'sa'
                        ? 'SA タイプのみ選択可能'
                        : r.status !== 'pending'
                          ? '未申請 (pending) のみ選択可能'
                          : '選択 (リマインダー送信用)'
                    }
                    className="size-3.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                  />
                </div>
                <div>
                  <Badge variant="default">{COMMISSION_LIST_LABELS[r.recipientType]}</Badge>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Link
                      to={paths.dealDetail.replace(':id', r.dealId)}
                      state={returnToState}
                      className="truncate font-medium text-[var(--text-primary)] hover:text-[var(--accent)]"
                    >
                      {r.dealTitle}
                    </Link>
                    {dealStages.data?.[r.dealId] && (
                      <Badge variant="default" className="shrink-0 text-[9px]">
                        {DEAL_STAGE_LABELS[dealStages.data[r.dealId]]}
                      </Badge>
                    )}
                  </div>
                  <div className="truncate text-[10px] text-[var(--text-muted)]">
                    {r.productName}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[var(--text-secondary)]">{r.contactName}</div>
                  <div className="truncate text-[10px] text-[var(--text-muted)]">
                    → {r.recipientName}
                  </div>
                </div>
                <div className="text-[var(--text-muted)] tabular-nums">{fmtDate(r.paidAt)}</div>
                <div
                  className="text-right font-semibold text-[var(--accent-green,_#3aaa55)] tabular-nums"
                  title={r.formula}
                >
                  ¥{r.finalAmount.toLocaleString()}
                </div>
                <div className="text-center">
                  {r.recipientType === 'referrer' ? (
                    <span className="text-[10px] text-[var(--text-muted)] italic">手動</span>
                  ) : r.bankAccountSnapshot ? (
                    <span className="text-[10px] text-[var(--text-secondary)]">
                      {r.bankAccountSnapshot.bankName}
                      <br />
                      {r.bankAccountSnapshot.accountNumber}
                    </span>
                  ) : (
                    <span className="text-[10px] text-[var(--accent-amber,_#d4a373)]">未設定</span>
                  )}
                </div>
                <div className="text-center">
                  <Badge variant={statusBadgeVariant(r.status)}>
                    {COMMISSION_RECORD_STATUS_LABELS[r.status]}
                  </Badge>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className="p-1.5 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-elevated)]"
                    title="編集"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  {r.status !== 'paid' && (
                    <button
                      type="button"
                      onClick={() => handleRequest(r)}
                      disabled={requestPayment.isPending}
                      className="p-1.5 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-elevated)] disabled:opacity-50"
                      title="支払い申請（経理へ通知）"
                    >
                      <Mail className="size-3.5" />
                    </button>
                  )}
                  {r.status === 'requested' && (
                    <button
                      type="button"
                      onClick={() => handleMarkPaid(r)}
                      disabled={markPaid.isPending}
                      className="p-1.5 rounded text-[var(--text-muted)] hover:text-[var(--accent-green)] hover:bg-[var(--bg-elevated)] disabled:opacity-50"
                      title="支払済にマーク"
                    >
                      <CheckCircle className="size-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-[var(--text-muted)] italic">
          編集ボタンで支払先口座・額調整・メモを編集。メールアイコンで経理へ支払い申請を送信（送り方は
          営業・通知設定 &gt; 自動通知）、 チェックアイコンで支払済マーク。
        </p>
      </div>

      <EditRecordDialog record={editing} onClose={() => setEditing(null)} />
    </Screen>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: 'amber' | 'blue' | 'green';
}) {
  const color =
    accent === 'amber'
      ? 'text-[var(--accent-amber,_#d4a373)]'
      : accent === 'blue'
        ? 'text-[var(--accent,_#3b82f6)]'
        : 'text-[var(--accent-green,_#3aaa55)]';
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${color}`}>¥{value.toLocaleString()}</div>
    </div>
  );
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-[var(--text-muted)]">{label}:</span>
      <div className="flex gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
              value === opt.value
                ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
